/**
 * Copyright (c) 2025 Andrew Berezovskyi
 *
 * SPDX-License-Identifier: EPL-2.0
 */
/**
 * Protocol types supported by OSLC delegated UIs
 */
var OslcProtocol;
(function (OslcProtocol) {
    OslcProtocol["WindowName"] = "#oslc-core-windowName-1.0";
    OslcProtocol["PostMessage"] = "#oslc-core-postMessage-1.0";
})(OslcProtocol || (OslcProtocol = {}));

/**
 * Copyright (c) 2025 Andrew Berezovskyi
 *
 * SPDX-License-Identifier: EPL-2.0
 */
/**
 * OSLC PostMessage Helper
 *
 * A focused TypeScript module for handling OSLC postMessage communication
 * in delegated UIs according to the OSLC specification section 4.3.
 *
 * @see https://docs.oasis-open-projects.org/oslc-op/core/v3.0/os/dialogs.html#messaging_conformance
 */
class OslcPostMessageHelper {
    /**
     * Sends an OSLC response using the appropriate protocol (window name or postMessage)
     *
     * @param response - The OSLC response object or string
     * @param protocol - Optional protocol to force, otherwise detected from URL hash
     */
    static sendResponse(response, protocol) {
        const responseString = typeof response === 'string'
            ? response
            : `${this.OSLC_RESPONSE_HEADER}${JSON.stringify(response)}`;
        const detectedProtocol = protocol || this.detectProtocol();
        switch (detectedProtocol) {
            case OslcProtocol.WindowName:
                this.respondWithWindowName(responseString);
                break;
            case OslcProtocol.PostMessage:
                this.respondWithPostMessage(responseString);
                break;
            default:
                console.warn('No OSLC protocol detected, defaulting to postMessage');
                this.respondWithPostMessage(responseString);
        }
    }
    /**
     * Sends a cancellation response with empty results
     */
    static sendCancelResponse() {
        const cancelResponse = { 'oslc:results': [] };
        this.sendResponse(cancelResponse);
    }
    /**
     * Sends a response for selected resources
     *
     * @param resources - Array of selected OSLC resources
     */
    static sendSelectionResponse(resources) {
        const response = { 'oslc:results': resources };
        this.sendResponse(response);
    }
    /**
     * Sends a response for a single created resource
     *
     * @param resource - The created OSLC resource
     */
    static sendCreationResponse(resource) {
        const response = { 'oslc:results': [resource] };
        this.sendResponse(response);
    }
    /**
     * Registers a listener for raw OSLC postMessage responses
     *
     * @param iframeElement - The iframe element containing the delegated UI
     * @param messageHandler - Handler function for the raw message
     * @returns Cleanup function to remove the event listener
     */
    static registerRawResponseListener(iframeElement, messageHandler) {
        const listener = (event) => {
            if (this.isValidOslcMessage(event, iframeElement)) {
                messageHandler(event.data);
            }
        };
        window.addEventListener('message', listener, false);
        return () => window.removeEventListener('message', listener, false);
    }
    /**
     * Registers a listener for OSLC selection dialog responses
     *
     * @param iframeElement - The iframe element containing the selection dialog
     * @param resourceHandler - Handler function for the selected resources
     * @param preprocessingHandler - Optional preprocessing function called before handling resources
     * @returns Cleanup function to remove the event listener
     */
    static registerSelectionListener(iframeElement, resourceHandler, preprocessingHandler) {
        const listener = (event) => {
            if (this.isValidOslcMessage(event, iframeElement)) {
                try {
                    const message = event.data.slice(this.OSLC_RESPONSE_HEADER.length);
                    const parsedResponse = JSON.parse(message);
                    if (preprocessingHandler) {
                        preprocessingHandler();
                    }
                    resourceHandler(parsedResponse['oslc:results']);
                }
                catch (error) {
                    console.error('Failed to parse OSLC response:', error);
                }
            }
        };
        window.addEventListener('message', listener, false);
        return () => window.removeEventListener('message', listener, false);
    }
    /**
     * Registers a listener for OSLC creation dialog responses
     *
     * @param iframeElement - The iframe element containing the creation dialog
     * @param resourceHandler - Handler function for the created resource
     * @param preprocessingHandler - Optional preprocessing function called before handling the resource
     * @returns Cleanup function to remove the event listener
     */
    static registerCreationListener(iframeElement, resourceHandler, preprocessingHandler) {
        const listener = (event) => {
            if (this.isValidOslcMessage(event, iframeElement)) {
                try {
                    const message = event.data.slice(this.OSLC_RESPONSE_HEADER.length);
                    const parsedResponse = JSON.parse(message);
                    if (preprocessingHandler) {
                        preprocessingHandler();
                    }
                    const resources = parsedResponse['oslc:results'];
                    if (resources.length > 0) {
                        const resource = resources[0];
                        resourceHandler(resource['rdf:resource'], resource['oslc:label']);
                    }
                }
                catch (error) {
                    console.error('Failed to parse OSLC response:', error);
                }
            }
        };
        window.addEventListener('message', listener, false);
        return () => window.removeEventListener('message', listener, false);
    }
    /**
     * Builds an OSLC dialog URL with the appropriate protocol fragment
     *
     * @param baseUrl - The base URL of the dialog
     * @param protocol - The protocol to use (defaults to postMessage)
     * @returns The complete dialog URL with protocol fragment
     */
    static buildDialogUrl(baseUrl, protocol = OslcProtocol.PostMessage) {
        const url = new URL(baseUrl);
        url.hash = protocol;
        return url.toString();
    }
    /**
     * Detects the OSLC protocol from the current window's URL hash
     */
    static detectProtocol() {
        const hash = window.location.hash;
        if (hash === OslcProtocol.WindowName) {
            return OslcProtocol.WindowName;
        }
        else if (hash === OslcProtocol.PostMessage) {
            return OslcProtocol.PostMessage;
        }
        return null;
    }
    /**
     * Checks if a message event is a valid OSLC response
     */
    static isValidOslcMessage(event, iframeElement) {
        return (event.source === iframeElement.contentWindow &&
            typeof event.data === 'string' &&
            event.data.indexOf(this.OSLC_RESPONSE_HEADER) === 0);
    }
    /**
     * Responds using the window name protocol
     */
    static respondWithWindowName(response) {
        let returnURL;
        try {
            returnURL = new URL(window.name, window.location.href);
        }
        catch {
            console.warn('Ignoring invalid OSLC Window Name return URL');
            return;
        }
        if (returnURL.protocol !== 'http:' && returnURL.protocol !== 'https:') {
            console.warn(`Ignoring OSLC Window Name return URL with ${returnURL.protocol} scheme`);
            return;
        }
        window.name = response;
        window.location.href = returnURL.href;
    }
    /**
     * Responds using the postMessage protocol
     */
    static respondWithPostMessage(response) {
        if (window.parent !== null) {
            window.parent.postMessage(response, '*');
        }
        else {
            window.postMessage(response, '*');
        }
    }
}
OslcPostMessageHelper.OSLC_RESPONSE_HEADER = 'oslc-response:';

export { OslcPostMessageHelper, OslcProtocol };
//# sourceMappingURL=index.esm.js.map
